import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Filter,
  Clock,
  CheckCircle2,
  XCircle,
  ChefHat,
  Truck,
  Coffee,
  MoreHorizontal,
  Eye,
  Printer,
  RefreshCw,
  UtensilsCrossed,
  Send,
  CreditCard,
  Receipt,
  Copy,
  Split,
  LayoutGrid,
  List,
  Timer,
  Trash2,
  RotateCcw,
  Calendar as CalendarIcon,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useOrders } from '@/hooks/useOrders';
import { useCurrency } from '@/hooks/useCurrency';
import { useLocationStore } from '@/store/location';
import { toast } from 'sonner';
import { OrderDetailsDialog } from '@/components/orders/OrderDetailsDialog';
import { AddPaymentDialog } from '@/components/orders/AddPaymentDialog';
import { EmailReceiptDialog } from '@/components/orders/EmailReceiptDialog';
import { SplitBillDialog } from '@/components/orders/SplitBillDialog';
import { VoidOrderDialog } from '@/components/orders/VoidOrderDialog';
import { RefundOrderDialog } from '@/components/orders/RefundOrderDialog';
import { useReceiptPrint } from '@/components/orders/ReceiptPrintView';
import { LocationSelector } from '@/components/location/LocationSelector';
import { ManagerPinDialog } from '@/components/ManagerPinDialog';
import { useReceiptPrints } from '@/hooks/useReceiptPrints';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { type DateRange } from 'react-day-picker';

type OrderStatus = 'open' | 'sent' | 'in_progress' | 'ready' | 'completed' | 'cancelled' | 'refunded';
type OrderType = 'dine_in' | 'takeout' | 'delivery' | 'bar_tab' | 'online';

const statusConfig: Record<OrderStatus, { label: string; color: string; icon: typeof Clock }> = {
  open: { label: 'Open', color: 'bg-gray-500', icon: Clock },
  sent: { label: 'Sent', color: 'bg-yellow-500', icon: Clock },
  in_progress: { label: 'Preparing', color: 'bg-blue-500', icon: ChefHat },
  ready: { label: 'Ready', color: 'bg-green-500', icon: CheckCircle2 },
  completed: { label: 'Completed', color: 'bg-gray-500', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: 'bg-red-500', icon: XCircle },
  refunded: { label: 'Refunded', color: 'bg-amber-500', icon: RotateCcw },
};

const typeConfig: Record<OrderType, { label: string; icon: typeof Coffee }> = {
  dine_in: { label: 'Dine-in', icon: Coffee },
  takeout: { label: 'Takeout', icon: UtensilsCrossed },
  delivery: { label: 'Delivery', icon: Truck },
  bar_tab: { label: 'Bar Tab', icon: Coffee },
  online: { label: 'Online', icon: Coffee },
};

type ViewMode = 'card' | 'list';

// Helper function to format time
const formatTime = (dateString: string | null) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

// Helper function to calculate order duration
const getOrderDuration = (createdAt: string | null, completedAt?: string | null): string => {
  if (!createdAt) return '—';
  const startTime = new Date(createdAt).getTime();
  const endTime = completedAt ? new Date(completedAt).getTime() : Date.now();
  const diffMs = endTime - startTime;
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return '<1m';
  if (diffMins < 60) return `${diffMins}m`;
  const hours = Math.floor(diffMins / 60);
  const mins = diffMins % 60;
  return `${hours}h ${mins}m`;
};

export default function Orders() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<OrderType | 'all'>('all');
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    // Always default to today - don't persist date ranges across sessions
    // as this causes confusion when orders from today don't appear
    const today = new Date();
    return { from: today, to: today };
  });
  const [onlyWithCustomer, setOnlyWithCustomer] = useState(false);
  const [onlyWithNotes, setOnlyWithNotes] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    // Load saved preference from localStorage, default to list view
    const saved = localStorage.getItem('orders-view-mode');
    return (saved === 'list' || saved === 'card') ? saved : 'list';
  });
  
  // Track if viewing all locations
  const [viewingAllLocations, setViewingAllLocations] = useState(false);

  // Save view mode preference when it changes
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem('orders-view-mode', mode);
  };

  
  // Dialog states
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showVoidDialog, setShowVoidDialog] = useState(false);
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [showSplitBillDialog, setShowSplitBillDialog] = useState(false);
  const [showReprintPinDialog, setShowReprintPinDialog] = useState(false);
  const [showEmailReceiptDialog, setShowEmailReceiptDialog] = useState(false);
  const [pendingPrintOrder, setPendingPrintOrder] = useState<any | null>(null);
  
  const { currentLocation } = useLocationStore();
  const { profile } = useAuth();
  
  // Determine if user can view all locations' orders
  const canViewAllLocations = profile?.role === 'superadmin' || 
    profile?.role === 'primary_admin' || 
    profile?.role === 'owner' ||
    profile?.role === 'admin';
  
  const {
    orders,
    isLoading,
    refetch,
    sendToKitchen,
    completeOrder,
    duplicateOrder,
    voidOrder,
    refundOrder,
    addPayment,
  } = useOrders(currentLocation?.id || null, { allLocations: viewingAllLocations && canViewAllLocations });
  const { formatCurrency } = useCurrency();
  const { printReceipt } = useReceiptPrint();
  const { hasOriginalPrint, reprintCount, logPrint } = useReceiptPrints(selectedOrder?.id || null);
  const { hasPermission, effectiveRole } = usePermissions();

  // Cashiers/employees only see their own orders
  const isCashier = effectiveRole === 'cashier' || effectiveRole === 'employee';

  // Permission checks for order actions
  const canVoidOrder = hasPermission('orders.cancel');
  const canEditOrder = hasPermission('orders.edit');
  const canCreateOrder = hasPermission('orders.create');
  const canPrintReceipt = hasPermission('pos.reprint_receipt');
  const canDuplicateOrder = hasPermission('orders.edit'); // Duplicate requires edit permission

  const handleSendToKitchen = async (orderId: string, orderNumber: string) => {
    try {
      await sendToKitchen.mutateAsync({ orderId });
      toast.success('Sent to kitchen', {
        description: `Order #${orderNumber} has been sent to the kitchen.`,
      });
    } catch (error: any) {
      toast.error('Error', {
        description: error.message || 'Failed to send order to kitchen.',
      });
    }
  };

  const handleCompleteOrder = async (orderId: string, orderNumber: string) => {
    try {
      await completeOrder.mutateAsync({ orderId });
      toast.success('Order completed', {
        description: `Order #${orderNumber} has been marked as completed.`,
      });
    } catch (error: any) {
      toast.error('Error', {
        description: error.message || 'Failed to complete order.',
      });
    }
  };

  const handleViewDetails = (order: any) => {
    setSelectedOrder(order);
    setShowDetailsDialog(true);
  };

  const handlePrintReceipt = async (order: any, skipApproval = false, approvedById?: string) => {
    // Check if this is a reprint
    const isReprint = hasOriginalPrint;

    // Cashiers always need approval to print receipts from Orders screen
    // Non-cashiers need approval only for reprints
    const needsApproval = isCashier ? true : isReprint;

    if (needsApproval && !skipApproval) {
      // Need manager approval
      setPendingPrintOrder(order);
      setSelectedOrder(order);
      setShowReprintPinDialog(true);
      return;
    }

    // Proceed with printing
    const success = await printReceipt({
      order,
      location: currentLocation ? {
        name: currentLocation.name,
        phone: currentLocation.phone,
      } : null,
      isReprint,
      printNumber: isReprint ? reprintCount + 1 : undefined,
    });

    if (success) {
      // Log the print with supervisor ID who approved
      if (currentLocation && profile) {
        await logPrint.mutateAsync({
          orderId: order.id,
          locationId: currentLocation.id,
          userId: profile.id,
          printType: isReprint ? 'reprint' : 'original',
          approvedById: approvedById,
        });
      }

      toast.success(isReprint ? 'Reprint sent' : 'Receipt printed', {
        description: `Receipt for order #${order.order_number} sent to printer.`,
      });
    } else {
      toast.error('Print failed', {
        description: 'Could not open print dialog. Please check your browser settings.',
      });
    }
  };

  const handleReprintApproved = async (managerId: string, _managerName: string) => {
    if (pendingPrintOrder) {
      await handlePrintReceipt(pendingPrintOrder, true, managerId);
      setPendingPrintOrder(null);
    }
    setShowReprintPinDialog(false);
  };

  const handleDuplicateOrder = async (order: any) => {
    try {
      const result = await duplicateOrder.mutateAsync({ orderId: order.id });
      toast.success('Order duplicated', {
        description: `New order created from #${result.originalOrderNumber}. The new order is open and ready to be sent to kitchen.`,
      });
    } catch (error: any) {
      toast.error('Error', {
        description: error.message || 'Failed to duplicate order.',
      });
    }
  };

  const handleViewInKitchen = (orderId: string) => {
    navigate(`/kds?orderId=${orderId}`);
  };

  const handleAddPayment = (order: any) => {
    setSelectedOrder(order);
    setShowPaymentDialog(true);
  };

  const handleSplitBill = (order: any) => {
    setSelectedOrder(order);
    setShowSplitBillDialog(true);
  };

  const handleProcessSplitPayments = async (
    orderId: string,
    payments: Array<{
      paymentMethod: 'cash' | 'card' | 'gift_card' | 'other';
      amount: number;
      tipAmount?: number;
    }>
  ) => {
    // Process each payment sequentially
    for (const payment of payments) {
      await addPayment.mutateAsync({
        orderId,
        paymentMethod: payment.paymentMethod,
        amount: payment.amount,
        tipAmount: payment.tipAmount,
      });
    }
    toast.success('Split payments processed', {
        description: `${payments.length} payment(s) added successfully.`,
      });
  };

  const handleProcessPayment = async (data: {
    orderId: string;
    paymentMethod: 'cash' | 'card' | 'gift_card' | 'other';
    amount: number;
    tipAmount?: number;
  }) => {
    try {
      await addPayment.mutateAsync({
        orderId: data.orderId,
        paymentMethod: data.paymentMethod,
        amount: data.amount,
        tipAmount: data.tipAmount,
      });
      toast.success('Payment added', {
        description: `Payment of ${formatCurrency(data.amount)} added successfully.`,
      });
    } catch (error: any) {
      throw error; // Let the dialog handle the error
    }
  };

  const handleVoidOrder = (order: any) => {
    setSelectedOrder(order);
    setShowVoidDialog(true);
  };

  const handleProcessVoid = async (data: {
    orderId: string;
    reason: string;
    approvedBy: string;
    restoreInventory: boolean;
  }) => {
    try {
      await voidOrder.mutateAsync(data);
      toast.success('Order voided', {
        description: `Order #${selectedOrder?.order_number} has been voided.${data.restoreInventory ? ' Inventory has been restored.' : ''}`,
      });
    } catch (error: any) {
      throw error; // Let the dialog handle the error
    }
  };

  const handleRefundOrder = (order: any) => {
    setSelectedOrder(order);
    setShowRefundDialog(true);
  };

  const handleProcessRefund = async (data: {
    orderId: string;
    amount: number;
    reason: string;
    refundMethod: string;
    approvedBy: string;
  }) => {
    try {
      await refundOrder.mutateAsync(data);
      const isFullRefund = data.amount >= (selectedOrder?.total || 0);
      toast.success(isFullRefund ? 'Order refunded' : 'Partial refund processed', {
        description: `${formatCurrency(data.amount)} refund for Order #${selectedOrder?.order_number} has been processed.`,
      });
    } catch (error: any) {
      throw error; // Let the dialog handle the error
    }
  };

  // Filter orders - cashiers only see their own orders
  const filteredOrders = orders.filter((order) => {
    // Cashiers can only see orders they created
    if (isCashier && profile?.id && order.user_id !== profile.id) return false;

    if (dateRange.from && order.created_at) {
      const created = new Date(order.created_at);
      const from = new Date(dateRange.from);
      from.setHours(0, 0, 0, 0);
      if (created < from) return false;
    }
    if (dateRange.to && order.created_at) {
      const created = new Date(order.created_at);
      const to = new Date(dateRange.to);
      to.setHours(23, 59, 59, 999);
      if (created > to) return false;
    }

    if (statusFilter !== 'all' && order.status !== statusFilter) return false;
    if (typeFilter !== 'all' && order.order_type !== typeFilter) return false;
    if (onlyWithCustomer && !order.customer) return false;
    if (onlyWithNotes && !order.notes) return false;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const customerName = order.customer
        ? `${order.customer.first_name || ''} ${order.customer.last_name || ''}`.trim()
        : '';
      return (
        order.order_number.toString().includes(query) ||
        customerName.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Handle location selection
  const handleLocationChange = (_locationId: string) => {
    setViewingAllLocations(false);
  };
  
  const handleAllLocationsSelect = () => {
    setViewingAllLocations(true);
  };

  const header = (
    <div className="flex items-center justify-between gap-4">
      <div>
        <h1 className="text-2xl font-display font-semibold tracking-tight">
          Sales history
        </h1>
        <p className="text-sm text-muted-foreground">
          View, edit and manage your sales all in one place.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <LocationSelector 
          showAllOption={canViewAllLocations} 
          onLocationSelect={handleLocationChange}
          onAllLocationsSelect={handleAllLocationsSelect}
        />
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
          Refresh
        </Button>
        <Badge variant="secondary" className="gap-1">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
          </span>
          Live
        </Badge>
      </div>
    </div>
  );

  return (
    <DashboardLayout header={header}>
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search orders..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="min-w-[180px]">
            <Select value={statusFilter} onValueChange={(val) => setStatusFilter(val as OrderStatus | 'all')}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {(['open', 'sent', 'in_progress', 'ready', 'completed', 'cancelled'] as OrderStatus[]).map((status) => (
                  <SelectItem key={status} value={status}>
                    <span className="inline-flex items-center gap-2">
                      <span className={cn('w-2 h-2 rounded-full', statusConfig[status].color)} />
                      {statusConfig[status].label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="min-w-[180px]">
            <Select value={typeFilter} onValueChange={(val) => setTypeFilter(val as OrderType | 'all')}>
              <SelectTrigger>
                <SelectValue placeholder="Order type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {(['dine_in', 'takeout', 'delivery', 'bar_tab', 'online'] as OrderType[]).map((type) => (
                  <SelectItem key={type} value={type}>
                    {typeConfig[type]?.label || type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <CalendarIcon className="h-4 w-4" />
                {dateRange.from && dateRange.to
                  ? dateRange.from.toDateString() === dateRange.to.toDateString()
                    ? dateRange.from.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : `${dateRange.from.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${dateRange.to.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                  : dateRange.from
                    ? dateRange.from.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : 'Pick dates'}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto p-3 space-y-3">
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    const today = new Date();
                    setDateRange({ from: today, to: today });
                  }}
                >
                  Today
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const today = new Date();
                    const last7 = new Date();
                    last7.setDate(today.getDate() - 6);
                    setDateRange({ from: last7, to: today });
                  }}
                >
                  Last 7 days
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const today = new Date();
                    const last30 = new Date();
                    last30.setDate(today.getDate() - 29);
                    setDateRange({ from: last30, to: today });
                  }}
                >
                  Last 30 days
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const today = new Date();
                    const start = new Date(today.getFullYear(), today.getMonth(), 1);
                    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                    setDateRange({ from: start, to: end });
                  }}
                >
                  This Month
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    const today = new Date();
                    setDateRange({ from: today, to: today });
                    setOnlyWithCustomer(false);
                    setOnlyWithNotes(false);
                  }}
                >
                  Clear
                </Button>
              </div>
              <Calendar
                mode="range"
                defaultMonth={dateRange.from}
                selected={dateRange}
                onSelect={(range) => {
                  if (range) {
                    setDateRange({ from: range.from, to: range.to });
                  }
                }}
                numberOfMonths={2}
                className="rounded-lg border shadow-sm"
              />
            </PopoverContent>
          </Popover>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Filter className="h-4 w-4" />
                More filters
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuLabel>Additional Filters</DropdownMenuLabel>
              <DropdownMenuCheckboxItem
                checked={onlyWithCustomer}
                onCheckedChange={(checked) => setOnlyWithCustomer(!!checked)}
              >
                Has customer
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={onlyWithNotes}
                onCheckedChange={(checked) => setOnlyWithNotes(!!checked)}
              >
                Has notes
              </DropdownMenuCheckboxItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  const today = new Date();
                  setDateRange({ from: today, to: today });
                  setOnlyWithCustomer(false);
                  setOnlyWithNotes(false);
                }}
              >
                Clear filters
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-1 ml-auto border rounded-md p-0.5">
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => handleViewModeChange('list')}
            className="h-7 w-7 p-0"
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'card' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => handleViewModeChange('card')}
            className="h-7 w-7 p-0"
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Loading skeleton */}
      {isLoading && (
        <div className="rounded-lg border overflow-hidden">
          <div className="bg-muted/50 border-b px-4 py-3">
            <div className="h-4 w-28 bg-muted animate-pulse rounded" />
          </div>
          <div className="divide-y">
            {Array.from({ length: 8 }).map((_, idx) => (
              <div
                key={idx}
                className={cn(
                  "grid gap-3 px-4 py-3",
                  viewMode === 'card'
                    ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
                    : "grid-cols-[100px_1fr_120px_120px_80px]"
                )}
              >
                {viewMode === 'card' ? (
                  <>
                    <div className="h-5 w-20 bg-muted animate-pulse rounded" />
                    <div className="h-5 w-32 bg-muted animate-pulse rounded" />
                    <div className="h-5 w-16 bg-muted animate-pulse rounded" />
                    <div className="h-5 w-24 bg-muted animate-pulse rounded" />
                    <div className="h-5 w-20 bg-muted animate-pulse rounded" />
                    <div className="h-5 w-28 bg-muted animate-pulse rounded" />
                  </>
                ) : (
                  <>
                    <div className="h-5 w-20 bg-muted animate-pulse rounded" />
                    <div className="space-y-2">
                      <div className="h-5 w-40 bg-muted animate-pulse rounded" />
                      <div className="h-4 w-32 bg-muted animate-pulse rounded" />
                    </div>
                    <div className="h-5 w-20 bg-muted animate-pulse rounded" />
                    <div className="h-5 w-20 bg-muted animate-pulse rounded" />
                    <div className="h-5 w-12 bg-muted animate-pulse rounded" />
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Orders Card View */}
      {!isLoading && viewMode === 'card' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredOrders.map((order) => {
            const status = order.status as OrderStatus;
            const orderType = order.order_type as OrderType;
            const StatusIcon = statusConfig[status]?.icon || Clock;
            const TypeIcon = typeConfig[orderType]?.icon || Coffee;
            const customerName = order.customer 
              ? `${order.customer.first_name || ''} ${order.customer.last_name || ''}`.trim()
              : null;
            const itemsCount = order.items?.length || 0;
            const duration = getOrderDuration(order.created_at, order.completed_at);

            return (
              <Card
                key={order.id}
                className={cn(
                  'relative overflow-hidden transition-all hover:shadow-lg cursor-pointer',
                  status === 'sent' && 'ring-2 ring-yellow-500/20',
                  status === 'ready' && 'ring-2 ring-green-500/20'
                )}
                onClick={() => handleViewDetails(order)}
              >
                {/* Status indicator bar */}
                <div
                  className={cn(
                    'absolute top-0 left-0 right-0 h-1',
                    statusConfig[status]?.color || 'bg-gray-500'
                  )}
                />

                <CardHeader className="pb-2 pt-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg font-semibold">
                        #{order.order_number}
                      </CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="gap-1 text-xs">
                          <TypeIcon className="h-3 w-3" />
                          {typeConfig[orderType]?.label || orderType}
                        </Badge>
                        {order.table?.name && (
                          <Badge variant="secondary" className="text-xs">
                            Table {order.table.name}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuLabel>Order #{order.order_number}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          className="gap-2 cursor-pointer"
                          onClick={(e) => { e.stopPropagation(); handleViewDetails(order); }}
                        >
                          <Eye className="h-4 w-4" />
                          View Details
                        </DropdownMenuItem>
                        {/* Print Receipt - for all users with permission (cashiers require supervisor PIN) */}
                        {canPrintReceipt && (
                          <DropdownMenuItem
                            className="gap-2 cursor-pointer"
                            onClick={(e) => { e.stopPropagation(); handlePrintReceipt(order); }}
                          >
                            <Receipt className="h-4 w-4" />
                            Print Receipt
                            {isCashier && <span className="ml-auto text-xs text-muted-foreground">PIN</span>}
                          </DropdownMenuItem>
                        )}
                        {canDuplicateOrder && (
                          <DropdownMenuItem
                            className="gap-2 cursor-pointer"
                            onClick={(e) => { e.stopPropagation(); handleDuplicateOrder(order); }}
                          >
                            <Copy className="h-4 w-4" />
                            Duplicate Order
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        {status === 'open' && canCreateOrder && (
                          <DropdownMenuItem
                            className="gap-2 cursor-pointer"
                            onClick={(e) => { e.stopPropagation(); handleSendToKitchen(order.id, order.order_number); }}
                          >
                            <Send className="h-4 w-4" />
                            Send to Kitchen
                          </DropdownMenuItem>
                        )}
                        {(status === 'sent' || status === 'in_progress') && (
                          <DropdownMenuItem 
                            className="gap-2 cursor-pointer"
                            onClick={(e) => { e.stopPropagation(); handleViewInKitchen(order.id); }}
                          >
                            <ChefHat className="h-4 w-4" />
                            View in Kitchen
                          </DropdownMenuItem>
                        )}
                        {!['completed', 'cancelled', 'refunded'].includes(status) && (
                          <>
                            {canEditOrder && (
                              <DropdownMenuItem
                                className="gap-2 cursor-pointer text-green-600"
                                onClick={(e) => { e.stopPropagation(); handleCompleteOrder(order.id, order.order_number); }}
                              >
                                <CheckCircle2 className="h-4 w-4" />
                                Complete Order
                              </DropdownMenuItem>
                            )}
                            {canEditOrder && (
                              <DropdownMenuItem
                                className="gap-2 cursor-pointer"
                                onClick={(e) => { e.stopPropagation(); handleAddPayment(order); }}
                              >
                                <CreditCard className="h-4 w-4" />
                                Add Payment
                              </DropdownMenuItem>
                            )}
                            {canEditOrder && (
                              <DropdownMenuItem
                                className="gap-2 cursor-pointer"
                                onClick={(e) => { e.stopPropagation(); handleSplitBill(order); }}
                              >
                                <Split className="h-4 w-4" />
                                Split Bill
                              </DropdownMenuItem>
                            )}
                            {canVoidOrder && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="gap-2 cursor-pointer text-red-600 focus:text-red-600"
                                  onClick={(e) => { e.stopPropagation(); handleVoidOrder(order); }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                  Void Order
                                </DropdownMenuItem>
                              </>
                            )}
                          </>
                        )}
                        {/* Refund - only for completed orders */}
                        {canVoidOrder && order.status === 'completed' && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="gap-2 cursor-pointer text-amber-600 focus:text-amber-600"
                              onClick={(e) => { e.stopPropagation(); handleRefundOrder(order); }}
                            >
                              <RotateCcw className="h-4 w-4" />
                              Refund Order
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>

                <CardContent className="space-y-3">
                  {customerName && (
                    <p className="text-sm font-medium">{customerName}</p>
                  )}
                  {order.server && (
                    <p className="text-xs text-muted-foreground">
                      Cashier: {order.server.first_name} {order.server.last_name?.charAt(0)}.
                    </p>
                  )}

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      {itemsCount} item{itemsCount !== 1 ? 's' : ''}
                    </span>
                    <span className="font-semibold">{formatCurrency(order.total ?? 0)}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {formatTime(order.created_at)}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Timer className="h-3 w-3" />
                        {status === 'completed' ? `Took ${duration}` : duration}
                      </div>
                    </div>
                    <Badge
                      variant="secondary"
                      className={cn(
                        'gap-1',
                        status === 'open' && 'bg-gray-500/10 text-gray-600',
                        status === 'sent' && 'bg-yellow-500/10 text-yellow-600',
                        status === 'in_progress' && 'bg-blue-500/10 text-blue-600',
                        status === 'ready' && 'bg-green-500/10 text-green-600',
                        status === 'completed' && 'bg-gray-500/10 text-gray-600',
                        status === 'cancelled' && 'bg-red-500/10 text-red-600'
                      )}
                    >
                      <StatusIcon className="h-3 w-3" />
                      {statusConfig[status]?.label || status}
                    </Badge>
                  </div>

                  <div className="flex gap-2 pt-2 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 gap-1"
                      onClick={(e) => { e.stopPropagation(); handleViewDetails(order); }}
                    >
                      <Eye className="h-3 w-3" />
                      View
                    </Button>
                    {/* Print button - only for non-cashiers (cashiers use reprint which requires manager PIN) */}
                    {canPrintReceipt && !isCashier && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1"
                        onClick={(e) => { e.stopPropagation(); handlePrintReceipt(order); }}
                      >
                        <Printer className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Orders List View */}
      {!isLoading && viewMode === 'list' && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 font-medium text-sm">Order #</th>
                  <th className="text-left p-3 font-medium text-sm">Type</th>
                  <th className="text-left p-3 font-medium text-sm">Customer</th>
                  <th className="text-left p-3 font-medium text-sm">Cashier</th>
                  <th className="text-left p-3 font-medium text-sm">Items</th>
                  <th className="text-left p-3 font-medium text-sm">Total</th>
                  <th className="text-left p-3 font-medium text-sm">Status</th>
                  <th className="text-left p-3 font-medium text-sm">Time</th>
                  <th className="text-left p-3 font-medium text-sm">Duration</th>
                  <th className="text-right p-3 font-medium text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) => {
                  const status = order.status as OrderStatus;
                  const orderType = order.order_type as OrderType;
                  const StatusIcon = statusConfig[status]?.icon || Clock;
                  const TypeIcon = typeConfig[orderType]?.icon || Coffee;
                  const customerName = order.customer 
                    ? `${order.customer.first_name || ''} ${order.customer.last_name || ''}`.trim()
                    : '—';
                  const serverName = order.server 
                    ? `${order.server.first_name || ''} ${order.server.last_name?.charAt(0) || ''}.`.trim()
                    : '—';
                  const itemsCount = order.items?.length || 0;
                  const duration = getOrderDuration(order.created_at, order.completed_at);

                  return (
                    <tr 
                      key={order.id} 
                      className={cn(
                        "border-b last:border-0 hover:bg-muted/30 transition-colors cursor-pointer",
                        status === 'cancelled' && 'opacity-60'
                      )}
                      onClick={() => handleViewDetails(order)}
                    >
                      <td className="p-3">
                        <span className="font-semibold">#{order.order_number}</span>
                        {order.table?.name && (
                          <span className="text-xs text-muted-foreground ml-2">
                            Table {order.table.name}
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        <Badge variant="outline" className="gap-1 text-xs">
                          <TypeIcon className="h-3 w-3" />
                          {typeConfig[orderType]?.label || orderType}
                        </Badge>
                      </td>
                      <td className="p-3 text-sm">{customerName}</td>
                      <td className="p-3 text-sm text-muted-foreground">{serverName}</td>
                      <td className="p-3 text-sm text-muted-foreground">
                        {itemsCount} item{itemsCount !== 1 ? 's' : ''}
                      </td>
                      <td className="p-3">
                        <span className="font-semibold">{formatCurrency(order.total ?? 0)}</span>
                      </td>
                      <td className="p-3">
                        <Badge
                          variant="secondary"
                          className={cn(
                            'gap-1',
                            status === 'open' && 'bg-gray-500/10 text-gray-600',
                            status === 'sent' && 'bg-yellow-500/10 text-yellow-600',
                            status === 'in_progress' && 'bg-blue-500/10 text-blue-600',
                            status === 'ready' && 'bg-green-500/10 text-green-600',
                            status === 'completed' && 'bg-gray-500/10 text-gray-600',
                            status === 'cancelled' && 'bg-red-500/10 text-red-600'
                          )}
                        >
                          <StatusIcon className="h-3 w-3" />
                          {statusConfig[status]?.label || status}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {formatTime(order.created_at)}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Timer className="h-3 w-3" />
                          {duration}
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuLabel>Order #{order.order_number}</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                className="gap-2 cursor-pointer"
                                onClick={() => handleViewDetails(order)}
                              >
                                <Eye className="h-4 w-4" />
                                View Details
                              </DropdownMenuItem>
                              {/* Print Receipt - for all users with permission (cashiers require supervisor PIN) */}
                              {canPrintReceipt && (
                                <DropdownMenuItem
                                  className="gap-2 cursor-pointer"
                                  onClick={() => handlePrintReceipt(order)}
                                >
                                  <Receipt className="h-4 w-4" />
                                  Print Receipt
                                  {isCashier && <span className="ml-auto text-xs text-muted-foreground">PIN</span>}
                                </DropdownMenuItem>
                              )}
                              {canDuplicateOrder && (
                                <DropdownMenuItem
                                  className="gap-2 cursor-pointer"
                                  onClick={() => handleDuplicateOrder(order)}
                                >
                                  <Copy className="h-4 w-4" />
                                  Duplicate Order
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              {status === 'open' && canCreateOrder && (
                                <DropdownMenuItem
                                  className="gap-2 cursor-pointer"
                                  onClick={() => handleSendToKitchen(order.id, order.order_number)}
                                >
                                  <Send className="h-4 w-4" />
                                  Send to Kitchen
                                </DropdownMenuItem>
                              )}
                              {(status === 'sent' || status === 'in_progress') && (
                                <DropdownMenuItem 
                                  className="gap-2 cursor-pointer"
                                  onClick={() => handleViewInKitchen(order.id)}
                                >
                                  <ChefHat className="h-4 w-4" />
                                  View in Kitchen
                                </DropdownMenuItem>
                              )}
                              {!['completed', 'cancelled', 'refunded'].includes(status) && (
                                <>
                                  {canEditOrder && (
                                    <DropdownMenuItem
                                      className="gap-2 cursor-pointer text-green-600"
                                      onClick={() => handleCompleteOrder(order.id, order.order_number)}
                                    >
                                      <CheckCircle2 className="h-4 w-4" />
                                      Complete Order
                                    </DropdownMenuItem>
                                  )}
                                  {canEditOrder && (
                                    <DropdownMenuItem
                                      className="gap-2 cursor-pointer"
                                      onClick={() => handleAddPayment(order)}
                                    >
                                      <CreditCard className="h-4 w-4" />
                                      Add Payment
                                    </DropdownMenuItem>
                                  )}
                                  {canEditOrder && (
                                    <DropdownMenuItem
                                      className="gap-2 cursor-pointer"
                                      onClick={() => handleSplitBill(order)}
                                    >
                                      <Split className="h-4 w-4" />
                                      Split Bill
                                    </DropdownMenuItem>
                                  )}
                                  {canVoidOrder && (
                                    <>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        className="gap-2 cursor-pointer text-red-600 focus:text-red-600"
                                        onClick={() => handleVoidOrder(order)}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                        Void Order
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                </>
                              )}
                              {/* Refund - only for completed orders */}
                              {canVoidOrder && order.status === 'completed' && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="gap-2 cursor-pointer text-amber-600 focus:text-amber-600"
                                    onClick={() => handleRefundOrder(order)}
                                  >
                                    <RotateCcw className="h-4 w-4" />
                                    Refund Order
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {!isLoading && filteredOrders.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            {orders.length === 0 ? (
              <Coffee className="h-8 w-8 text-muted-foreground" />
            ) : (
              <Filter className="h-8 w-8 text-muted-foreground" />
            )}
          </div>
          <h3 className="font-medium">
            {orders.length === 0 ? 'No sales yet' : 'No sales found'}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {orders.length === 0
              ? 'Sales will appear here when completed'
              : 'Try adjusting your filters or search query'
            }
          </p>
        </div>
      )}

      {/* Order Details Dialog */}
      <OrderDetailsDialog
        open={showDetailsDialog}
        onOpenChange={setShowDetailsDialog}
        order={selectedOrder}
        onPrintReceipt={() => {
          setShowDetailsDialog(false);
          if (selectedOrder) handlePrintReceipt(selectedOrder);
        }}
        onGiftReceipt={() => {
          // Gift receipt functionality - same as print but marked as gift
          setShowDetailsDialog(false);
          if (selectedOrder) handlePrintReceipt(selectedOrder);
        }}
        onEmailReceipt={() => {
          setShowDetailsDialog(false);
          if (selectedOrder) setShowEmailReceiptDialog(true);
        }}
        onVoid={canVoidOrder ? () => {
          setShowDetailsDialog(false);
          if (selectedOrder) handleVoidOrder(selectedOrder);
        } : undefined}
        onReturnItems={canVoidOrder ? () => {
          setShowDetailsDialog(false);
          if (selectedOrder) handleRefundOrder(selectedOrder);
        } : undefined}
      />

      {/* Email Receipt Dialog */}
      <EmailReceiptDialog
        open={showEmailReceiptDialog}
        onOpenChange={setShowEmailReceiptDialog}
        order={selectedOrder}
      />

      {/* Add Payment Dialog */}
      <AddPaymentDialog
        open={showPaymentDialog}
        onOpenChange={setShowPaymentDialog}
        order={selectedOrder}
        onAddPayment={handleProcessPayment}
        isProcessing={addPayment.isPending}
      />

      {/* Split Bill Dialog */}
      <SplitBillDialog
        open={showSplitBillDialog}
        onOpenChange={setShowSplitBillDialog}
        order={selectedOrder}
        onProcessPayments={handleProcessSplitPayments}
        isProcessing={addPayment.isPending}
      />

      {/* Void Order Dialog */}
      <VoidOrderDialog
        open={showVoidDialog}
        onOpenChange={setShowVoidDialog}
        order={selectedOrder}
        onVoidOrder={handleProcessVoid}
        isProcessing={voidOrder.isPending}
      />

      {/* Refund Order Dialog */}
      <RefundOrderDialog
        open={showRefundDialog}
        onOpenChange={setShowRefundDialog}
        order={selectedOrder}
        onRefundOrder={handleProcessRefund}
        isProcessing={refundOrder.isPending}
      />

      {/* Manager PIN Dialog for receipt printing */}
      <ManagerPinDialog
        open={showReprintPinDialog}
        onOpenChange={setShowReprintPinDialog}
        onVerified={handleReprintApproved}
        title="Supervisor Approval Required"
        description={
          isCashier
            ? `Supervisor PIN required to print receipt for order #${pendingPrintOrder?.order_number || ''}.`
            : `A receipt has already been printed for this order. Supervisor approval is required to print again.`
        }
      />
    </DashboardLayout>
  );
}
